import { Document, DOCUMENT_MAX_SIZE, DOCUMENT_MIME_TYPES, FieldConfig } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useRef, useState } from 'react';

import {
  Button,
  DropZone,
  DropZoneButton,
  Flex,
  Heading,
  Input,
  Label,
  Separator,
  Text,
  TextField,
} from '@/components/core';
import { AIDocumentIcon, CheckmarkFilledIcon, CloseIcon, DocumentIcon, SaveIcon } from '@/components/icons';
import { Step, StepProps } from '@/features/agents';
import { useCreateDocument, usePutFile } from '@/hooks/api';

import { useModelSuggestion } from './useModelSuggestion';

const DocumentLoading = memo(() => {
  return (
    <Flex className="bg-gray-25 relative size-16 items-center justify-center rounded-full">
      <AIDocumentIcon className="absolute inset-0 h-full w-full animate-pulse" />
    </Flex>
  );
});

const UploadDocumentCTA = memo(() => {
  return (
    <Flex col className="gap-xs items-center">
      <AIDocumentIcon className="!size-16" />
      <Heading>Click to upload a sample document</Heading>
      <Text className="!font-regular">(pdf, jpg, png. Max 25 mb)</Text>
    </Flex>
  );
});

type FileUploadProgress = {
  file?: File;
  status?: 'uploading' | 'success' | 'error';
};

type SampleDocumentItemProps = FileUploadProgress & {
  onClear: () => void;
};

const SampleDocumentItem = ({ file, status, onClear }: SampleDocumentItemProps) => {
  return (
    <Flex col className="p-sm gap-xs rounded-lg border-1 border-gray-200 bg-gray-50">
      <Flex className="gap-sm items-start">
        <Flex className="relative items-center justify-center">
          <DocumentIcon className="!size-8 text-gray-400" />
          {status === 'uploading' ? (
            <SaveIcon className="absolute top-[50%] left-[50%] size-3 -translate-x-[50%] -translate-y-[50%]" />
          ) : status === 'success' ? (
            <CheckmarkFilledIcon className="text-success-500 absolute top-[50%] left-[50%] !size-3 -translate-x-[50%] -translate-y-[50%]" />
          ) : null}
        </Flex>

        <Flex col className="grow">
          <Text className="font-semibold">{file?.name}</Text>
          <Text variant="secondary" className="text-xs">
            {status === 'success'
              ? 'Succeeded'
              : status === 'uploading'
                ? 'Uploading ...'
                : status === 'error'
                  ? 'Failed'
                  : 'Pending'}
          </Text>
        </Flex>

        <Button variant="plain" onPress={onClear}>
          <CloseIcon />
        </Button>
      </Flex>
    </Flex>
  );
};

export type ConfigureCustomModelResult = {
  fieldConfig?: FieldConfig | null;
  agentName?: string;
  sampleDocuments?: File[] | null;
};

type GeneratingModelProgressProps = {
  title: string;
  description: string;
};

const GeneratingModelProgress = memo(({ title, description }: GeneratingModelProgressProps) => {
  return (
    <Flex col className="gap-md items-center">
      <DocumentLoading />

      <Flex col className="gap-xs items-center">
        <Heading>{title}</Heading>
        <Text>{description}</Text>
      </Flex>
    </Flex>
  );
});

export type ConfigureCustomModelStepProps = StepProps & {
  onContinue: (result: ConfigureCustomModelResult) => void;
};

const IConfigureCustomModelStep = ({ onContinue, ...rest }: ConfigureCustomModelStepProps) => {
  const [documents, setDocuments] = useState<Document[]>([]);

  const [isUploading, setIsUploading] = useState(false);
  const [isGeneratingModel, setIsGeneratingModel] = useState(false);
  const [files, setFiles] = useState<FileUploadProgress[]>([]);

  const { mutateAsync: createDocument } = useCreateDocument();
  const { mutateAsync: putDocumentFile } = usePutFile();
  const { createFieldConfig } = useModelSuggestion();

  const inputRef = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onGenerateModelFromDocuments = useCallback(() => {
    setIsGeneratingModel(true);

    createFieldConfig(documents, description)
      .then(({ fieldConfig, modelName }) => {
        if (Object.keys(fieldConfig ?? {}).length > 0) {
          const sampleDocuments = files.map((f) => f.file).filter((f) => f !== undefined);
          onContinue({ fieldConfig, agentName: modelName, sampleDocuments });
        } else {
          setError('Unable to setup model. Please try again with a different document.');
        }
      })
      .finally(() => {
        setIsGeneratingModel(false);
      });
  }, [createFieldConfig, description, documents, files, onContinue]);

  const onUploadFiles = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      setFiles(files.map((file) => ({ file, status: 'uploading' })));
      setError(null);

      const uploads = files.map(async (file) => {
        try {
          const document = await createDocument({ name: file.name });
          await putDocumentFile({ fileUrl: document.fileUrl, file });
          setFiles((prev) => prev.map((f) => (f.file?.name === file.name ? { file, status: 'success' } : f)));

          inputRef.current?.focus();
          return document;
        } catch {
          setFiles((prev) => prev.map((f) => (f.file?.name === file.name ? { file, status: 'error' } : f)));
        }
      });

      try {
        const documents = await Promise.all(uploads);
        setDocuments(documents.filter((d) => d !== undefined));
      } finally {
        setIsUploading(false);
      }
    },
    [createDocument, putDocumentFile]
  );

  const isReady = documents.length > 0 && description.trim().length > 0;

  return (
    <Step {...rest}>
      <Flex col className="gap-md">
        <Flex col className="gap-lg px-md h-72">
          <Flex col className="gap-xs">
            <Heading size="md">Setup your AI model</Heading>
            <Text>Give a short description and upload a sample document to setup your AI model.</Text>
          </Flex>

          {isGeneratingModel ? (
            <GeneratingModelProgress
              title="Setting up your model ..."
              description="Setting up fields, formatters and validators for your model."
            />
          ) : (
            <Flex col className="gap-md grow">
              <Flex col>
                <TextField onChange={setDescription}>
                  <Label isRequired>Document type</Label>
                  <Input placeholder="E.g. Tax Deduction Form" ref={inputRef} />
                </TextField>
              </Flex>

              <Flex col className="grow">
                <Label isRequired>Sample document</Label>

                {files.length === 0 ? (
                  <DropZone
                    acceptedMimeTypes={[...DOCUMENT_MIME_TYPES].filter((mimeType) => mimeType !== 'message/rfc822')}
                    maxFileSize={DOCUMENT_MAX_SIZE}
                    onDropFiles={onUploadFiles}
                    isDisabled={isUploading}
                    className="grow"
                  >
                    <DropZoneButton>
                      <UploadDocumentCTA />
                    </DropZoneButton>
                  </DropZone>
                ) : (
                  <Flex col className="gap-xs grow">
                    {files.map((file) => (
                      <SampleDocumentItem
                        key={file.file?.name}
                        {...file}
                        onClear={() => setFiles((files) => files.filter((f) => f.file?.name !== file.file?.name))}
                      />
                    ))}
                  </Flex>
                )}
              </Flex>

              <Flex col>
                {error && (
                  <Flex variant="error" className="p-sm rounded-md">
                    <Text>{error || 'Erorr message here'}</Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>

        <Separator orientation="horizontal" />

        <Flex col className="px-md">
          <Button variant="primary" isDisabled={!isReady || isGeneratingModel} onPress={onGenerateModelFromDocuments}>
            {isGeneratingModel && <SaveIcon />}
            <Text>Continue</Text>
          </Button>
        </Flex>
      </Flex>
    </Step>
  );
};

export const ConfigureCustomModelStep = memo(IConfigureCustomModelStep) as typeof IConfigureCustomModelStep;
